import {InstanceInterface} from "../Interfaces/InstanceInterface";
import {AttributeInterface} from "../Interfaces/AttributeInterface";
import {DataTypesEnum} from "../enums/dataTypesEnum";
import {AttributeTypeEnum} from "../enums/AttributeTypeEnum";
import {Attribute} from "./Attribute";
import {RelationshipInterface} from "../Interfaces/RelationshipInterface";
import {Relationship} from "./Relationship";
import {BusinessObjectBaseInterface} from "../Interfaces/BusinessObjectBaseInterface";
import {InstanceRelationshipInterface} from "../Interfaces/InstanceRelationshipInterface";

const hash = require('object-hash');

export class Instance implements InstanceInterface {
    private _id: number;
    private _name: string;
    private _boClass: BusinessObjectBaseInterface;
    private _relationships: Map<number, { order: number; relType: RelationshipInterface; value: Map<number, { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }> }> = new Map();
    private _attributes: Map<number, { attribute: AttributeInterface; order: number; value: string | string[] | number }> = new Map();
    private _tags: string[];
    private _created: string;
    private _updated: string;
    private _updatedByUser: string;
    private _fromRelationships: any[] = [];
    private _toRelationships: any[] = [];

    constructor(id: number, name: string, boClass: BusinessObjectBaseInterface, attributes: { attribute: AttributeInterface; order: number; value: string | string[] | number }[], relationships: { order: number; relType: RelationshipInterface; value: { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }[] }[], tags: string[], created: string, updated: string, updatedByUser: string) {
        this._id = id;
        this._name = name;
        this._boClass = boClass;
        this.addRelationships(relationships);
        this.addAttributes(attributes);
        this._tags = tags;
        this._created = created;
        this._updated = updated;
        this._updatedByUser = updatedByUser;
    }

    addAttributes(attributes: { attribute: AttributeInterface; order: number; value: any }[]): void {
        attributes.forEach(attrObject => {
            attrObject.attribute = new Attribute(attrObject.attribute.id, attrObject.attribute.name, attrObject.attribute.description, attrObject.attribute.unitOfMeasure,
                attrObject.attribute.type, attrObject.attribute.minLength, attrObject.attribute.maxLength, attrObject.attribute.maxDigitsLeftOfDecimal,
                attrObject.attribute.minDigitsRightOfDecimal, attrObject.attribute.regexValidation, attrObject.attribute.uiSection, attrObject.attribute.prefix,
                attrObject.attribute.suffix, attrObject.attribute.receiveNotifications, attrObject.attribute.boClass, attrObject.attribute.attributeValues, attrObject.attribute.required, attrObject.attribute.systemRequired,
                attrObject.attribute.defaultValue, attrObject.attribute.lookupAttributeId, attrObject.attribute.createRelationships, attrObject.attribute.lookupRelTypeId);
            switch (attrObject.attribute.type) {
                case AttributeTypeEnum.VALUE_LIST: {
                    if (!attrObject.value) {
                        attrObject.value = [attrObject.attribute.attributeValues.get(attrObject.attribute.defaultValue as number).name];
                    }
                }
                    break;
                case AttributeTypeEnum.INT: {
                    if (isNaN(attrObject.value)) {
                        attrObject.value = attrObject.attribute.defaultValue;
                    } else {
                        attrObject.value = parseInt(attrObject.value, 10);
                    }
                }
                    break;

                case AttributeTypeEnum.DECIMAL: {
                    if (isNaN(attrObject.value)) {
                        attrObject.value = attrObject.attribute.defaultValue;
                    } else {
                        attrObject.value = parseFloat(attrObject.value);
                    }
                }
                    break;
                case AttributeTypeEnum.JSON: {
                    if (!attrObject.value) {
                        attrObject.value = attrObject.attribute.defaultValue;
                    }
                }
                    break;
                case AttributeTypeEnum.INCREMENTING: {
                    if (!attrObject.value) {
                        attrObject.value = "";
                    }
                }
                    break;
                case AttributeTypeEnum.LOOKUP:
                    if (!attrObject.value) {
                        attrObject.value = {};
                    }
                    break;
                default: {
                    if (attrObject.value === null) {
                        attrObject.value = attrObject.attribute.defaultValue;
                    }
                }
            }
            this._attributes.set(attrObject.attribute.id, $.extend(true, {}, attrObject));
        })
    }

    addRelationships(relationships: { order: number; relType: RelationshipInterface; value: { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }[] }[]): void {
        relationships.forEach(relationship => {
            this.addRelationship(relationship);
        });
    }

    addRelationship(relationship: { order: number; relType: RelationshipInterface; value: { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }[] }): void {
        const relType = new Relationship(relationship.relType.id, relationship.relType.name, relationship.relType.inward, relationship.relType.outward, relationship.relType.fromBoClass, relationship.relType.toBoClass, relationship.relType.systemRequired);
        const valueMap = new Map();
        relationship.value.forEach(val => {
            valueMap.set(val.id, val);
        });
        this.relationships.set(relType.id, {order: relationship.order, relType: relType, value: valueMap})
    }

    get id(): number {
        return this._id;
    }

    set id(value: number) {
        this._id = value;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get boClass(): BusinessObjectBaseInterface {
        return this._boClass;
    }

    set boClass(value: BusinessObjectBaseInterface) {
        this._boClass = value;
    }

    get attributes(): Map<number, { attribute: AttributeInterface; order: number; value: string | string[] | number }> {
        return this._attributes;
    }

    set attributes(value: Map<number, { attribute: AttributeInterface; order: number; value: string | string[] | number }>) {
        this._attributes = value;
    }

    get fromRelationships(): any[] {
        return this._fromRelationships;
    }

    set fromRelationships(value: any[]) {
        this._fromRelationships = value;
    }


    get toRelationships(): any[] {
        return this._toRelationships;
    }

    set toRelationships(value: any[]) {
        this._toRelationships = value;
    }

    get relationships(): Map<number, { order: number; relType: RelationshipInterface; value: Map<number, { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }> }> {
        return this._relationships;
    }

    set relationships(value: Map<number, { order: number; relType: RelationshipInterface; value: Map<number, { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }> }>) {
        this._relationships = value;
    }

    get tags(): string[] {
        return this._tags;
    }

    set tags(value: string[]) {
        this._tags = value;
    }

    get created(): string {
        return this._created;
    }

    set created(value: string) {
        this._created = value;
    }

    get updated(): string {
        return this._updated;
    }

    set updated(value: string) {
        this._updated = value;
    }

    get updatedByUser(): string {
        return this._updatedByUser;
    }

    set updatedByUser(value: string) {
        this._updatedByUser = value;
    }

    clone(): Instance {
        return new Instance(this.id, this.name, this.boClass, <{ attribute: AttributeInterface; order: number; value: string | string[] | number }[]>this.getAttributes(DataTypesEnum.ARRAY), <{ order: number; relType: RelationshipInterface; value: { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }[] }[]>this.getRelationships(), this.tags, this.created, this.updated, this.updatedByUser);
    }

    equal(instance: Instance): boolean {
        if (!instance) {
            return false;
        }
        if (this.name.trim() !== instance.name.trim()) {
            return false;
        }
        const attributesArray = <{ attribute: AttributeInterface; order: number; value: string | string[] | number }[]>this.getAttributes(DataTypesEnum.ARRAY);
        for (let i = 0; i < attributesArray.length; i++) {
            const attrObj1 = attributesArray[i];
            const attrObj2 = instance.getAttribute(attrObj1.attribute.id);
            switch (attrObj1.attribute.type) {
                case AttributeTypeEnum.TEXT:
                case AttributeTypeEnum.JSON:
                case AttributeTypeEnum.DATE:
                case AttributeTypeEnum.TIME:
                case AttributeTypeEnum.TIMESTAMP:
                case AttributeTypeEnum.INCREMENTING: {
                    if ((<string>attrObj1.value).trim() !== (<string>attrObj2.value).trim()) {
                        return false
                    }
                }
                    break;
                case AttributeTypeEnum.INT:

                case AttributeTypeEnum.DECIMAL: {
                    if ((<number>attrObj1.value) !== (<number>attrObj2.value)) {
                        return false
                    }
                }
                    break;
                case AttributeTypeEnum.VALUE_LIST: {
                    const attrVal1 = (<string[]>attrObj1.value);
                    const attrVal2 = (<string[]>attrObj2.value);

                    if (attrVal1.length !== attrVal2.length) {
                        return false;
                    }

                    for (let i = 0; i < attrVal1.length; i++) {
                        const v1 = attrVal1[i];
                        if (attrVal2.indexOf(v1) === -1) {
                            return false;
                        }
                    }
                }
                    break;
                case AttributeTypeEnum.LOOKUP: {
                    const attrVal1 = (<string[]>attrObj1.value);
                    const attrVal2 = (<string[]>attrObj2.value);
                    if (hash(attrVal1) !== hash(attrVal2)) {
                        return false;
                    }
                }
            }
        }
        const tags1 = this.tags;
        const tags2 = instance.tags;
        if (tags1.length !== tags2.length) {
            return false;
        }
        for (let i = 0; i < tags1.length; i++) {
            if (tags2.indexOf(tags1[i]) === -1) {
                return false
            }
        }
        return true;
    }

    isRemovable(): boolean {
        return false;
    }

    isValid(): { valid: boolean; msg: string } {
        if (this.getAttributeByName("Name").attribute.type !== AttributeTypeEnum.INCREMENTING && this.name.trim() === "") {
            return {valid: false, msg: "The instance name can't be an empty string."};
        }
        const result = this.isValidAttributes();
        if (!result.valid) {
            return result;
        }
        return {valid: true, msg: undefined};
    }

    isValidAttributes(): { valid: boolean; msg: string } {
        const attributesArray = (<{ attribute: AttributeInterface, order: number, value: string | string[] | number }[]>this.getAttributes(DataTypesEnum.ARRAY));
        for (let i = 0; i < attributesArray.length; i++) {
            if (attributesArray[i].attribute.name !== "Name") {
                const result = this.isValidAttribute(attributesArray[i]);
                if (!result.valid) {
                    return result;
                }
            }
        }
        return {valid: true, msg: null};
    }

    getAttributes(type: string): { attribute: AttributeInterface; order: number; value: any }[] | Map<number, { attribute: AttributeInterface; order: number; value: any }> {
        switch (type) {
            case DataTypesEnum.ARRAY: {
                return Array.from(this._attributes.values())
            }
            case DataTypesEnum.MAP: {
                return this._attributes
            }
        }
    }

    getAttribute(id: number): { attribute: AttributeInterface, order: number, value: any } {
        return this._attributes.get(id);
    }

    checkInstanceBeforeSave(): { valid: boolean; msg: string } {
        const result = this.isValid();
        if (!result.valid) {
            return result;
        }
        return {valid: true, msg: undefined};
    }

    isValidAttribute(attrObject: { attribute: AttributeInterface; order: number; value: string | string[] | number }): { valid: boolean; msg: string } {
        if (attrObject.value === "" && !attrObject.attribute.required) {
            return {valid: true, msg: undefined};
        } else {
            switch (attrObject.attribute.type) {
                case AttributeTypeEnum.TEXT: {
                    if (attrObject.value === "") {
                        return {valid: false, msg: `Attribute ${attrObject.attribute.name} can't be an empty string.`};
                    }
                    if (attrObject.attribute.minLength !== -1 && attrObject.value.toString().length < attrObject.attribute.minLength) {
                        return {
                            valid: false,
                            msg: `Attribute "${attrObject.attribute.name}" Min Length is invalid. Current length is: ${attrObject.value.toString().length}; Min Length is: ${attrObject.attribute.minLength}`
                        };
                    }
                    if (attrObject.attribute.maxLength !== -1 && attrObject.value.toString().length > attrObject.attribute.maxLength) {
                        return {
                            valid: false,
                            msg: `Attribute "${attrObject.attribute.name}" Max Length is invalid. Current length is: ${attrObject.value.toString().length}; Max Length is: ${attrObject.attribute.maxLength}`
                        };
                    }
                    if (!attrObject.value.toString().match(attrObject.attribute.regexValidation)) {
                        return {
                            valid: false,
                            msg: `The provided value: "${attrObject.value}" doesn't satisfy the regex validation.`
                        };
                    }
                }
                    break;
                case AttributeTypeEnum.INT: {
                    // @ts-ignore
                    if (isNaN(parseInt(attrObject.value, 10))) {
                        return {valid: false, msg: `Attribute ${attrObject.attribute.name} must be a number`};
                    }
                    if (attrObject.attribute.minLength !== -1 && attrObject.value < attrObject.attribute.minLength) {
                        return {
                            valid: false,
                            msg: `Attribute "${attrObject.attribute.name}" Min Value is invalid. Current value is: ${attrObject.value}; Min Value is: ${attrObject.attribute.minLength}`
                        };
                    }
                    if (attrObject.attribute.maxLength !== -1 && attrObject.value > attrObject.attribute.maxLength) {
                        return {
                            valid: false,
                            msg: `Attribute "${attrObject.attribute.name}" Max Value is invalid. Current value is: ${attrObject.value}; Max Value is: ${attrObject.attribute.maxLength}`
                        };
                    }
                    // @ts-ignore
                    attrObject.value = parseInt(attrObject.value, 10)
                }
                    break;

                case AttributeTypeEnum.DECIMAL: {
                    // @ts-ignore
                    if (isNaN(parseFloat(attrObject.value))) {
                        return {valid: false, msg: `Attribute ${attrObject.attribute.name} must be a number`};
                    }
                    if (attrObject.attribute.minLength !== -1 && attrObject.value < attrObject.attribute.minLength) {
                        return {
                            valid: false,
                            msg: `Attribute "${attrObject.attribute.name}" Min Value is invalid. Current value is: ${attrObject.value}; Min Value is: ${attrObject.attribute.minLength}`
                        };
                    }
                    if (attrObject.attribute.maxLength !== -1 && attrObject.value > attrObject.attribute.maxLength) {
                        return {
                            valid: false,
                            msg: `Attribute "${attrObject.attribute.name}" Max Value is invalid. Current value is: ${attrObject.value}; Max Value is: ${attrObject.attribute.maxLength}`
                        };
                    }

                    if (attrObject.attribute.maxDigitsLeftOfDecimal !== -1) {
                        const maxDigLeft = attrObject.attribute.maxDigitsLeftOfDecimal;
                        const regex = new RegExp(`^\\d{0,${maxDigLeft}}(\\.\\d{${0},${Number.MAX_SAFE_INTEGER}})?$`, "g");

                        if (!attrObject.value.toString().match(regex)) {
                            return {
                                valid: false,
                                msg: `Attribute "${attrObject.attribute.name}" does not match the Max Digits Left Of Decimal validation.`
                            };
                        }
                    }

                    if (attrObject.attribute.minDigitsRightOfDecimal > 0) {
                        const maxDigLeft = attrObject.attribute.maxDigitsLeftOfDecimal === -1 ? Number.MAX_SAFE_INTEGER : attrObject.attribute.maxDigitsLeftOfDecimal;
                        const minDigRight = attrObject.attribute.minDigitsRightOfDecimal;
                        const regex = new RegExp(`^\\d{0,${maxDigLeft}}\\.\\d{${minDigRight},${Number.MAX_SAFE_INTEGER}}?$`, "g");

                        if (!attrObject.value.toString().match(regex)) {
                            return {
                                valid: false,
                                msg: `Attribute "${attrObject.attribute.name}" does not match the Min Digits Right Of Decimal validation.`
                            };
                        }
                    }
                    // @ts-ignore
                    attrObject.value = parseFloat(attrObject.value);
                }
                    break;
                case AttributeTypeEnum.JSON: {
                    try {
                        if (attrObject.attribute.minLength !== -1 && attrObject.value.toString().length < attrObject.attribute.minLength) {
                            return {
                                valid: false,
                                msg: `Attribute "${attrObject.attribute.name}" Min Length is invalid. Current length is: ${attrObject.value.toString().length}; Min Length is: ${attrObject.attribute.minLength}`
                            };
                        }
                        if (attrObject.attribute.maxLength !== -1 && attrObject.value.toString().length > attrObject.attribute.maxLength) {
                            return {
                                valid: false,
                                msg: `Attribute "${attrObject.attribute.name}" Max Length is invalid. Current length is: ${attrObject.value.toString().length}; Max Length is: ${attrObject.attribute.maxLength}`
                            };
                        }
                        if (!attrObject.value.toString().match(attrObject.attribute.regexValidation)) {
                            return {
                                valid: false,
                                msg: `The provided value: "${attrObject.value}" doesn't satisfy the regex validation.`
                            };
                        }
                        // @ts-ignore
                        JSON.parse(attrObject.value);
                    } catch (e) {
                        return {valid: false, msg: `Attribute ${attrObject.attribute.name} is not a valid json.`};
                    }
                }
                    break;
                case AttributeTypeEnum.DATE:
                case AttributeTypeEnum.TIME:
                case AttributeTypeEnum.TIMESTAMP: {
                    if (attrObject.value === "") {
                        return {valid: false, msg: `Attribute ${attrObject.attribute.name} can't be an empty string.`};
                    }
                }
                    break;
                case AttributeTypeEnum.VALUE_LIST: {
                    if ((attrObject.value as Array<string>).length === 0 && attrObject.attribute.required) {
                        return {valid: false, msg: `Attribute ${attrObject.attribute.name} can't be an empty array.`};
                    }
                }
                    break;
                case AttributeTypeEnum.INCREMENTING: {

                }
            }
        }
        return {valid: true, msg: undefined};
    }

    getRelationships(): { order: number; relType: RelationshipInterface; value: { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }[] }[] {
        return Array.from(this.relationships.values()).map(val => {
            return {order: val.order, relType: val.relType, value: Array.from(val.value.values())}
        })
    }

    getRelationshipType(id: number): { order: number; relType: RelationshipInterface; value: Map<number, { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface, toBoInstance: InstanceRelationshipInterface }> } {
        return this.relationships.get(id);
    }

    hasRelationshipType(id: number): boolean {
        return this.relationships.has(id);
    }

    getRelationshipByRelTypeName(name: string): { order: number; relType: RelationshipInterface; value: { id: number; created: string; updated: string; fromBoInstance: InstanceRelationshipInterface; toBoInstance: InstanceRelationshipInterface }[] } {
        const relationships = this.getRelationships();
        for (let i = 0; i < relationships.length; i++) {
            if (relationships[i].relType.name === name) {
                return relationships[i];
            }
        }
        return null;
    }

//TODO
    hasRelationship(id: number): boolean {
        const relationships = this.getRelationships();
        for (let i = 0; i < relationships.length; i++) {
            for (let j = 0; j < relationships[i].value.length; j++) {
                if (relationships[i].value[j].fromBoInstance.id === id || relationships[i].value[j].toBoInstance.id === id) {
                    return true
                }
            }
        }
        return false;
    }

    getRelatedInstanceIds(): Set<number> {
        const relationships = this.getRelationships();
        const idsSet: Set<number> = new Set();
        for (let i = 0; i < relationships.length; i++) {
            for (let j = 0; j < relationships[i].value.length; j++) {
                if (this.id !== relationships[i].value[j].fromBoInstance.id) {
                    idsSet.add(relationships[i].value[j].fromBoInstance.id);
                } else {
                    idsSet.add(relationships[i].value[j].toBoInstance.id);
                }
            }
        }
        return idsSet
    }

    toDiffObject(): object {
        const diffObj = {tags: this.tags.toString()};
        Array.from(this.attributes.values()).forEach(item => {
            switch (item.attribute.type) {
                case AttributeTypeEnum.VALUE_LIST: {
                    diffObj[item.attribute.name] = item.value as string[]
                }
                    break;
                default:
                    diffObj[item.attribute.name] = item.value;
            }
        });
        return diffObj
    }

    getAttributeByName(name: string): { attribute: AttributeInterface; order: number; value: string | string[] | number } {
        const attributes = Array.from(this.attributes.values());
        for (let i = 0; i < attributes.length; i++) {
            if (attributes[i].attribute.name === name) {
                return attributes[i];
            }
        }
        return null
    }
}


// WEBPACK FOOTER //
// ./app/components/bo-core/models/Instance.ts